import { createReducer, createActions } from 'reduxsauce';
import Immutable from 'seamless-immutable';
import { convertObjToArr, sortArrByKey } from '../../utils';

/* Types & Action Creators */
const { Types, Creators } = createActions({
  getCourseInfos: [],
  requestCourse: ['courseId'],
  requestNextLesson: ['courseId'],
  requestCoursesList: [],
  requestCoursesListFull: [],
  requestLessonFiles: ['courseId', 'lessonId'],
  requestLessonQuestions: ['courseId', 'lessonId'],
  requestCertificateHtml: ['courseId'],

  setActiveCourse: ['courseId', 'lessonId'],
  setCourseError: ['errorType', 'error'],
  setCourseLoading: ['loadingType', 'loading'],
  setCoursesList: ['courses'],
  setNextLesson: ['courseId', 'nextLesson'],
  setLessonAnswers: ['courseId', 'lessonId', 'questions'],
  setLessonComments: ['courseId', 'lessonId', 'comments'],
  setLessonCompleted: ['courseId', 'lessonId', 'isCompleted'],
  setCertificateHtml: ['curseId', 'html'],
  setCertificateHtmlError: ['curseId', 'error'],

  setLessonError: ['errorType', 'error', 'course', 'lesson'],

  updateCoursesList: ['course'],
  updateLessonCompleted: ['courseId', 'lessonId', 'isCompleted'],
  updateLessonFiles: ['files', 'course', 'lesson'],
  updateLessonQuestions: ['questions', 'course', 'lesson'],
  updateLessonComments: ['comments', 'course', 'lesson'],
  updateLessonQuestionAnswer: ['data', 'course', 'lesson'],
  updateLessonAnnotationsStatus: ['status', 'course', 'lesson'],
  removeLessonComment: ['commentId', 'course', 'lesson'],
  registerLessonAccessed: ['lessonId'],
});
export const CourseTypes = Types;
export default Creators;

/* Initial State */
export const INITIAL_STATE = Immutable({
  list: {},
  allCoursesLoaded: false,
  current: {
    course: null,
    lesson: null,
  },
  loading: {
    list: false,
    current: false,
  },
  error: {
    list: null,
    current: null,
  },
});

/* Reducers */
const setActive = (state = INITIAL_STATE, { courseId, lessonId }) =>
  Immutable.updateIn(state, ['current'], current =>
    Immutable.merge(current, {
      course: courseId,
      lesson: lessonId,
    }),
  );

const setCourseError = (state = INITIAL_STATE, { errorType, error }) =>
  Immutable.setIn(state, ['error', errorType], error);

const setCourseLoading = (state = INITIAL_STATE, { loadingType, loading }) =>
  Immutable.setIn(state, ['loading', loadingType], loading);

const setCoursesList = (state = INITIAL_STATE, { courses }) => {
  const coursesIds = Object.keys(courses);
  const mergedCourses = coursesIds.reduce((accumulator, courseItemId) => {
    const courseInfos = courses[courseItemId];
    return {
      ...accumulator,
      [courseItemId]: {
        ...state.list[parseInt(courseItemId, 10)],
        ...courseInfos,
      },
    };
  }, {});
  return Immutable.merge(state, {
    list: mergedCourses,
    allCoursesLoaded: true,
  });
};

const setNextLesson = (state = INITIAL_STATE, { courseId, nextLesson }) =>
  Immutable.updateIn(state, ['list', courseId], course =>
    Immutable.merge(course, { nextLesson }),
  );

const setLessonComments = (
  state = INITIAL_STATE,
  { courseId, lessonId, comments },
) =>
  Immutable.updateIn(state, ['list', courseId, 'lessons', lessonId], lesson =>
    Immutable.merge(lesson, { comments, commentsLoaded: true }),
  );

const setCertificateHtml = (state = INITIAL_STATE, { courseId, html }) =>
  Immutable.updateIn(state, ['list', courseId, 'certificate'], certificate =>
    Immutable.set(certificate, 'html', html),
  );

const setCertificateHtmlError = (state = INITIAL_STATE, { courseId, error }) =>
  Immutable.updateIn(state, ['list', courseId, 'certificate'], certificate =>
    Immutable.set(certificate, 'error', error),
  );

const updateCoursesList = (state = INITIAL_STATE, { course }) => {
  return Immutable.updateIn(state, ['list'], list =>
    Immutable.merge(list, {
      [course.id]: course,
    }),
  );
};

const updateLessonCompleted = (
  state = INITIAL_STATE,
  { courseId, lessonId, isCompleted },
) => {
  const courseInfos = state.list[courseId];
  const lessonsArr = sortArrByKey(
    convertObjToArr(courseInfos.lessons),
    'order',
  );
  const completedAmmount =
    lessonsArr.filter(lesson => lesson.isConcluded).length +
    (isCompleted ? 1 : -1);
  const conclusionPercent = parseInt(
    (completedAmmount / lessonsArr.length) * 100,
    10,
  );
  const nextLesson = (() => {
    const lessons = isCompleted
      ? lessonsArr.filter(
          lesson =>
            !lesson.isConcluded && Number(lesson.id) !== Number(lessonId),
        )
      : lessonsArr.filter(
          lesson =>
            !lesson.isConcluded || Number(lesson.id) === Number(lessonId),
        );
    return lessons.length
      ? {
          id: lessons[0].id,
          title: lessons[0].title,
          daysToRelease: lessons[0].daysToRelease,
        }
      : false;
  })();
  const updatedCertificate = (() => {
    if (!courseInfos.certificate) return false;
    const {
      daysToRelease: releaseDays,
      conclusionPercent: releasePercent,
    } = courseInfos.certificate;
    return {
      ...courseInfos.certificate,
      locked: !!releaseDays || releasePercent > conclusionPercent,
    };
  })();

  return Immutable.updateIn(state, ['list', courseId], course =>
    Immutable.merge(course, {
      conclusionPercent,
      nextLesson,
      certificate: updatedCertificate,
      lessons: Immutable.updateIn(course.lessons, [lessonId], lesson =>
        Immutable.merge(lesson, { isConcluded: isCompleted }),
      ),
    }),
  );
};

const updateLessonFiles = (state = INITIAL_STATE, { files, course, lesson }) =>
  Immutable.updateIn(state, ['list', course, 'lessons', lesson], lessonInfos =>
    Immutable.merge(lessonInfos, {
      files,
      filesLoaded: true,
    }),
  );

const updateLessonComments = (
  state = INITIAL_STATE,
  { comments, course, lesson },
) =>
  Immutable.updateIn(
    state,
    ['list', course, 'lessons', lesson, 'comments'],
    currentComments => Immutable.merge(currentComments, comments),
  );

const updateLessonQuestions = (
  state = INITIAL_STATE,
  { data, course, lesson },
) =>
  Immutable.updateIn(state, ['list', course, 'lessons', lesson], lessonInfos =>
    Immutable.merge(lessonInfos, { ...data, questionsLoaded: true }),
  );

const updateLessonQuestionAnswer = (
  state = INITIAL_STATE,
  { data: { questionId, text }, course, lesson },
) =>
  Immutable.updateIn(
    state,
    ['list', course, 'lessons', lesson, 'questions', questionId, 'answer'],
    answer => Immutable.merge(answer, { text }),
  );

const updateLessonAnnotationsStatus = (
  state = INITIAL_STATE,
  { status, course, lesson },
) =>
  Immutable.updateIn(state, ['list', course, 'lessons', lesson], lessonInfos =>
    Immutable.merge(lessonInfos, { annotationsLoaded: status }),
  );

const removeLessonComment = (
  state = INITIAL_STATE,
  { commentId, course, lesson },
) =>
  Immutable.updateIn(
    state,
    ['list', course, 'lessons', lesson, 'comments'],
    comments => Immutable.without(comments, commentId),
  );

const setLessonError = (
  state = INITIAL_STATE,
  { errorType, error, course, lesson },
) =>
  Immutable.updateIn(state, ['list', course, 'lessons', lesson], lessonInfos =>
    Immutable.merge(lessonInfos, {
      error: lessonInfos.error
        ? Immutable.merge(lessonInfos.error, { [errorType]: error })
        : { [errorType]: error },
    }),
  );

/* Reducers to types */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.SET_ACTIVE_COURSE]: setActive,
  [Types.SET_COURSE_ERROR]: setCourseError,
  [Types.SET_COURSE_LOADING]: setCourseLoading,
  [Types.SET_COURSES_LIST]: setCoursesList,
  [Types.SET_NEXT_LESSON]: setNextLesson,
  [Types.SET_LESSON_COMMENTS]: setLessonComments,
  [Types.SET_CERTIFICATE_HTML]: setCertificateHtml,
  [Types.SET_CERTIFICATE_HTML_ERROR]: setCertificateHtmlError,
  [Types.UPDATE_COURSES_LIST]: updateCoursesList,
  [Types.UPDATE_LESSON_COMPLETED]: updateLessonCompleted,
  [Types.UPDATE_LESSON_FILES]: updateLessonFiles,
  [Types.UPDATE_LESSON_COMMENTS]: updateLessonComments,
  [Types.UPDATE_LESSON_QUESTIONS]: updateLessonQuestions,
  [Types.UPDATE_LESSON_QUESTION_ANSWER]: updateLessonQuestionAnswer,
  [Types.UPDATE_LESSON_ANNOTATIONS_STATUS]: updateLessonAnnotationsStatus,
  // [Types.REGISTER_LESSON_ACCESSED]: registerLessonAccessed,
  [Types.REMOVE_LESSON_COMMENT]: removeLessonComment,
  [Types.SET_LESSON_ERROR]: setLessonError,
});
